import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../shared/api';
import Card from '../../components/Card';
import { CenteredLoader, InlineLoader } from '../../components/LoadingSpinner';
import '../../shared/ReactSelectDarkMode.css';

const CustomerForm = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isEdit = Boolean(customerId);

  const [formData, setFormData] = useState({
    company_name: '',
    company_id: '',
    resp_person: '',
    street: '',
    city: '',
    postal_code: '',
    tax_id: '',
    telephone: '',
    mobile: '',
    email: '',
    discount: 0,
    description: ''
  });

  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [aresLoading, setAresLoading] = useState(false);
  const [aresError, setAresError] = useState(null);

  const { isLoading: loading, data: existingCustomer } = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => api.get(`customers/api/customers/${customerId}/`),
    enabled: isEdit,
  });

  // Populate form when customer data loads
  useEffect(() => {
    if (existingCustomer) {
      setFormData({
        company_name: existingCustomer.company_name || '',
        company_id: existingCustomer.company_id || '',
        resp_person: existingCustomer.resp_person || '',
        street: existingCustomer.street || '',
        city: existingCustomer.city || '',
        postal_code: existingCustomer.postal_code || '',
        tax_id: existingCustomer.tax_id || '',
        telephone: existingCustomer.telephone || '',
        mobile: existingCustomer.mobile || '',
        email: existingCustomer.email || '',
        discount: existingCustomer.discount || 0,
        description: existingCustomer.description || ''
      });
    }
  }, [existingCustomer]);

  const saveMutation = useMutation({
    mutationFn: (customerData) => isEdit
      ? api.put(`customers/api/customers/${customerId}/`, customerData)
      : api.post('customers/api/customers/', customerData),
    onSuccess: () => navigate('/customers'),
    onError: (err) => {
      if (err.response?.data) {
        setErrors(err.response.data);
      } else {
        setError(isEdit ? t('customers.errors.failedToUpdate') : t('customers.errors.failedToCreate'));
      }
    },
  });
  const submitting = saveMutation.isPending;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setError(null);

    const customerData = {
      ...formData,
      discount: parseFloat(formData.discount) || 0
    };

    saveMutation.mutate(customerData);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const fetchFromAres = async () => {
    if (!formData.company_id) return;
    setAresLoading(true);
    setAresError(null);
    try {
      const res = await fetch(`https://ares.gov.cz/ekonomicke-subjekty-v-be/rest/ekonomicke-subjekty/${encodeURIComponent(formData.company_id)}`);
      if (!res.ok) throw new Error('not found');
      const data = await res.json();
      const sidlo = data.sidlo || {};
      const streetParts = [sidlo.nazevUlice, sidlo.cisloDomovni, sidlo.cisloOrientacni].filter(Boolean);
      setFormData(prev => ({
        ...prev,
        company_name: data.obchodniJmeno || prev.company_name,
        tax_id: data.dic || prev.tax_id,
        street: streetParts.length ? streetParts.join(' ') : prev.street,
        city: sidlo.nazevObce || prev.city,
        postal_code: sidlo.psc ? String(sidlo.psc) : prev.postal_code,
      }));
    } catch {
      setAresError(t('customers.aresError'));
    } finally {
      setAresLoading(false);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const getDiscountStatus = () => {
    const discount = parseFloat(formData.discount) || 0;

    if (discount === 0) {
      return {
        type: 'secondary',
        title: t('customers.discountInfo.none'),
        message: t('customers.discountInfo.noneMessage')
      };
    } else if (discount <= 5) {
      return {
        type: 'info',
        title: t('customers.discountInfo.low'),
        message: `${discount}% ${t('customers.discountInfo.applied')}`
      };
    } else if (discount <= 15) {
      return {
        type: 'warning',
        title: t('customers.discountInfo.medium'),
        message: `${discount}% ${t('customers.discountInfo.applied')}`
      };
    } else {
      return {
        type: 'success',
        title: t('customers.discountInfo.high'),
        message: `${discount}% ${t('customers.discountInfo.applied')}`
      };
    }
  };

  if (loading) {
    return (
      <div className="container-xl">
        <CenteredLoader message={t('customers.loading')} />
      </div>
    );
  }

  const discountStatus = getDiscountStatus();

  return (
    <div className="container-xl">
      {/* Page Header */}
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/customers" className="text-reset">
                        {t('customers.title')}
                      </Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      {isEdit ? t('customers.editCustomer') : t('customers.addCustomer')}
                    </li>
                  </ol>
                </nav>
              </div>
              <h2 className="page-title">
                {isEdit ? t('customers.editCustomer') : t('customers.addCustomer')}
              </h2>
              <div className="page-subtitle">
                {isEdit ? t('customers.editCustomerDescription') : t('customers.addCustomerDescription')}
              </div>
            </div>
            <div className="col-auto ms-auto d-print-none">
              <div className="btn-list">
                <Link
                  to="/customers"
                  className="btn btn-outline-secondary"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-arrow-left" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                    <path d="M5 12l14 0"></path>
                    <path d="M5 12l6 6"></path>
                    <path d="M5 12l6 -6"></path>
                  </svg>
                  {t('common.back')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Body */}
      <div className="page-body">
        <div className="container-xl">
          {error && (
            <div className="alert alert-danger alert-dismissible" role="alert">
              <div className="d-flex">
                <div>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    strokeLinecap="round" strokeLinejoin="round"
                    className="icon alert-icon">
                    <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                    <path d="M12 9v2m0 4v.01"/>
                    <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"/>
                  </svg>
                </div>
                <div className="ms-2">{error}</div>
              </div>
              <button className="btn-close" data-bs-dismiss="alert" aria-label="close"></button>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="row row-deck row-cards">
              {/* Company Information - Full Width */}
              <div className="col-12">
                <Card>
                  <div className="card-header">
                    <h3 className="card-title">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M3 21l18 0"/>
                        <path d="M5 21v-16l2.5 -1.5l2.5 1.5v16"/>
                        <path d="M9 21v-13l2.5 -1.5l2.5 1.5v13"/>
                        <path d="M13 21v-10l2.5 -1.5l2.5 1.5v10"/>
                      </svg>
                      {t('customers.companyInformation')}
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label required">{t('customers.fields.companyName')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.company_name ? 'is-invalid' : ''}`}
                            value={formData.company_name}
                            onChange={(e) => handleInputChange('company_name', e.target.value)}
                            required
                            placeholder={t('customers.placeholders.companyName')}
                          />
                          {errors?.company_name && (
                            <div className="invalid-feedback">{Array.isArray(errors.company_name) ? errors.company_name[0] : errors.company_name}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.companyId')}</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className={`form-control ${errors?.company_id ? 'is-invalid' : ''}`}
                              value={formData.company_id}
                              onChange={(e) => handleInputChange('company_id', e.target.value)}
                              placeholder={t('customers.placeholders.companyId')}
                            />
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={fetchFromAres}
                              disabled={aresLoading || !formData.company_id}
                            >
                              {aresLoading ? (
                                <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              ) : null}
                              {t('customers.aresButton')}
                            </button>
                          </div>
                          {errors?.company_id && (
                            <div className="invalid-feedback d-block">{Array.isArray(errors.company_id) ? errors.company_id[0] : errors.company_id}</div>
                          )}
                          {aresError && (
                            <div className="text-danger small mt-1">{aresError}</div>
                          )}
                          <small className="form-hint">
                            {t('customers.hints.companyId')}
                          </small>
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.respPerson')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.resp_person ? 'is-invalid' : ''}`}
                            value={formData.resp_person}
                            onChange={(e) => handleInputChange('resp_person', e.target.value)}
                            placeholder={t('customers.placeholders.respPerson')}
                          />
                          {errors?.resp_person && (
                            <div className="invalid-feedback">{Array.isArray(errors.resp_person) ? errors.resp_person[0] : errors.resp_person}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.taxId')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.tax_id ? 'is-invalid' : ''}`}
                            value={formData.tax_id}
                            onChange={(e) => handleInputChange('tax_id', e.target.value)}
                            placeholder={t('customers.placeholders.taxId')}
                          />
                          {errors?.tax_id && (
                            <div className="invalid-feedback">{Array.isArray(errors.tax_id) ? errors.tax_id[0] : errors.tax_id}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.description')}</label>
                          <textarea
                            className={`form-control ${errors?.description ? 'is-invalid' : ''}`}
                            rows="3"
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            placeholder={t('customers.placeholders.description')}
                          />
                          {errors?.description && (
                            <div className="invalid-feedback">{Array.isArray(errors.description) ? errors.description[0] : errors.description}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Address Information - Full Width */}
              <div className="col-12">
                <Card>
                  <div className="card-header">
                    <h3 className="card-title">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M12 20l-3 -3h2a4 4 0 1 0 -2 -7.5l-.15 .005a4 4 0 0 0 -3.85 3.995a4 4 0 0 0 2 3.5h2z"/>
                        <path d="M17 8l2 -2"/>
                        <path d="M21 3l-6.5 6.5a1 1 0 0 1 -1.5 0a1 1 0 0 1 0 -1.5l6.5 -6.5"/>
                      </svg>
                      {t('customers.addressInformation')}
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.street')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.street ? 'is-invalid' : ''}`}
                            value={formData.street}
                            onChange={(e) => handleInputChange('street', e.target.value)}
                            placeholder={t('customers.placeholders.street')}
                          />
                          {errors?.street && (
                            <div className="invalid-feedback">{Array.isArray(errors.street) ? errors.street[0] : errors.street}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.postalCode')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.postal_code ? 'is-invalid' : ''}`}
                            value={formData.postal_code}
                            onChange={(e) => handleInputChange('postal_code', e.target.value)}
                            placeholder={t('customers.placeholders.postalCode')}
                          />
                          {errors?.postal_code && (
                            <div className="invalid-feedback">{Array.isArray(errors.postal_code) ? errors.postal_code[0] : errors.postal_code}</div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-12">
                        <div className="mb-3">
                          <label className="form-label">{t('customers.fields.city')}</label>
                          <input
                            type="text"
                            className={`form-control ${errors?.city ? 'is-invalid' : ''}`}
                            value={formData.city}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            placeholder={t('customers.placeholders.city')}
                          />
                          {errors?.city && (
                            <div className="invalid-feedback">{Array.isArray(errors.city) ? errors.city[0] : errors.city}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Contact Information - Half Width */}
              <div className="col-lg-6">
                <Card>
                  <div className="card-header">
                    <h3 className="card-title">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M5 4h4l2 5l-2.5 1.5a11 11 0 0 0 5 5l1.5 -2.5l5 2v4a2 2 0 0 1 -2 2a16 16 0 0 1 -15 -15a2 2 0 0 1 2 -2"/>
                      </svg>
                      {t('customers.contactInformation')}
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">{t('customers.fields.telephone')}</label>
                      <input
                        type="tel"
                        className={`form-control ${errors?.telephone ? 'is-invalid' : ''}`}
                        value={formData.telephone}
                        onChange={(e) => handleInputChange('telephone', e.target.value)}
                        placeholder={t('customers.placeholders.telephone')}
                      />
                      {errors?.telephone && (
                        <div className="invalid-feedback">{Array.isArray(errors.telephone) ? errors.telephone[0] : errors.telephone}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('customers.fields.mobile')}</label>
                      <input
                        type="tel"
                        className={`form-control ${errors?.mobile ? 'is-invalid' : ''}`}
                        value={formData.mobile}
                        onChange={(e) => handleInputChange('mobile', e.target.value)}
                        placeholder={t('customers.placeholders.mobile')}
                      />
                      {errors?.mobile && (
                        <div className="invalid-feedback">{Array.isArray(errors.mobile) ? errors.mobile[0] : errors.mobile}</div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">{t('customers.fields.email')}</label>
                      <input
                        type="email"
                        className={`form-control ${errors?.email ? 'is-invalid' : ''} ${formData.email && !validateEmail(formData.email) ? 'is-invalid' : ''}`}
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder={t('customers.placeholders.email')}
                      />
                      {errors?.email && (
                        <div className="invalid-feedback">{Array.isArray(errors.email) ? errors.email[0] : errors.email}</div>
                      )}
                      {formData.email && !validateEmail(formData.email) && !errors?.email && (
                        <div className="invalid-feedback">{t('customers.validation.invalidEmail')}</div>
                      )}
                    </div>
                  </div>
                </Card>
              </div>

              {/* Discount - Half Width */}
              <div className="col-lg-6">
                <Card>
                  <div className="card-header">
                    <h3 className="card-title">
                      <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                        <path d="M7 9m0 2a2 2 0 0 1 2 -2h6a2 2 0 0 1 2 2v6a2 2 0 0 1 -2 2h-6a2 2 0 0 1 -2 -2z"/>
                        <path d="M14 14m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>
                        <path d="M17 9v-2a2 2 0 0 0 -2 -2h-6a2 2 0 0 0 -2 2v2"/>
                      </svg>
                      {t('customers.discount')}
                    </h3>
                  </div>
                  <div className="card-body">
                    <div className="mb-3">
                      <label className="form-label">{t('customers.fields.discount')} (%)</label>
                      <input
                        type="number"
                        className={`form-control ${errors?.discount ? 'is-invalid' : ''}`}
                        value={formData.discount}
                        onChange={(e) => handleInputChange('discount', e.target.value)}
                        min="0"
                        max="100"
                        step="any"
                        placeholder="0"
                      />
                      {errors?.discount && (
                        <div className="invalid-feedback">{Array.isArray(errors.discount) ? errors.discount[0] : errors.discount}</div>
                      )}
                      <small className="form-hint">
                        {t('customers.hints.discount')}
                      </small>
                    </div>

                    {formData.discount > 0 && (
                      <div className={`alert alert-${discountStatus.type} py-2 px-3`}>
                        <small>
                          <strong>{discountStatus.title}</strong><br />
                          {discountStatus.message}
                        </small>
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            </div>

            <div className="row mt-4">
              <div className="col-12">
                <Card>
                  <div className="card-footer bg-transparent mt-auto">
                    <div className="btn-list justify-content-end">
                      <Link
                        to="/customers"
                        className="btn btn-outline-secondary"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                          <path d="M4 9l16 0"/>
                          <path d="M4 15l16 0"/>
                          <path d="M12 3l-8 4l8 4l8 -4z"/>
                        </svg>
                        {t('common.cancel')}
                      </Link>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <InlineLoader className="me-2" />
                            {isEdit ? t('common.updating') : t('common.creating')}
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="icon me-2" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                              <path d="M6 4h10l4 4v10a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2"/>
                              <circle cx="12" cy="14" r="2"/>
                              <polyline points="14,4 14,8 8,8 8,4"/>
                            </svg>
                            {isEdit ? t('common.update') : t('common.create')}
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerForm;
